import { useRef, useEffect, useState, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  ZoomControl,
  useMap,
  Tooltip
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import LocateButton from "./LocateButton";
import { GPS_SVG } from "../../utils/svgToString";


// const DEBUG_TRACK = {
//   drawpoints:true,
//   drawlines:true,
//   normalize: true, // This must be true. Do not disable.
//   removeDuplicates: true,
//   removeJumps: true,
//   smooth: false , //Enabling this removes edge points. For example at the destination. 
//   simplify: true,
//   idleCheck: true
// };

const DEBUG_TRACK = {
  drawpoints:false,
  drawlines:true,
  normalize: true, // This must be true. Do not disable.
  removeDuplicates: false,
  removeJumps: true,
  smooth: true , //Enabling this removes edge points. For example at the destination. 
  simplify: true,
  idleCheck: true
};

/* =========================================================
   SMART MOVEMENT LOCK ENGINE
   Strategy:
   1. Ignore movement until:
      - distance(anchor, current) >= 200m
      - OR avg speed >= 5 km/h
   2. Then start tracking normally
========================================================= */

// const MOVEMENT_CONFIG = {
//   unlockDistance: 200, // meters
//   unlockSpeed: 4,      // km/h
//   speedWindow: 3       // last N points
// };


/* =========================================================
   ICONS
========================================================= */  

const startIconStatic = L.divIcon({
  html: `<div class="marker-dot start-dot"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7]
});

const endIconStatic = L.divIcon({
  html: `<div class="marker-dot end-dot"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7]
});

const movingIcon = L.divIcon({
  html: `
    <div class="gpstracking">
      <div class="gps-pulse"></div>
      <div class="gpstracking">${GPS_SVG}</div>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 20]
});

const idleIcon = L.divIcon({
  html: `
    <div style="
      width:14px;
      height:14px;
      border-radius:50%;
      background:#f59e0b;
      box-shadow:0 0 6px rgba(0,0,0,.4);
      border:1px solid white;
    "></div>
  `,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  className: ""
});

const highlightIcon = L.divIcon({
  html: `
    <div style="
      width:16px;
      height:16px;
      border-radius:50%;
      background:#7c3aed;
      box-shadow:0 0 0 3px rgba(124,58,237,.35);
      animation:pulseMarker 1.4s infinite;
    "></div>
  `,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  className: ""
});

function makeVisitIcon() {
  return L.divIcon({
    html: `
      <div style="
        width:14px;
        height:14px;
        border-radius:50%;
        background:#ef4444;
        box-shadow:0 0 5px rgba(0,0,0,.4);
      "></div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    className: ""
  });
}

function fmtIdleDuration(secs) {
  if (!secs) return "";
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  return `${h > 0 ? `${h} hr ` : ""}${m} min`;
}

/* =========================================================
   HELPERS
========================================================= */

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });
}

function getTs(row) {
  return new Date(
    row.captured_Date ||
      row.CapturedDate ||
      row.timestamp ||
      row.DateTime
  ).getTime();
}

function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = v => (v * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getSpeedKmh(p1, p2) {
  const meters = getDistance(
    p1.lat, p1.lng,
    p2.lat, p2.lng
  );

  const sec = (p2.ts - p1.ts) / 1000;
  if (sec <= 0) return 0;

  return (meters / sec) * 3.6;
}

function avgRecentSpeed(points, n = 3) {
  if (points.length < 2) return 0;

  const start = Math.max(1, points.length - n);
  let speeds = [];

  for (let i = start; i < points.length; i++) {
    speeds.push(getSpeedKmh(points[i - 1], points[i]));
  }

  if (!speeds.length) return 0;

  return speeds.reduce((a, b) => a + b, 0) / speeds.length;
}


/* ---------------------------------------------------------
   MAIN FILTER
--------------------------------------------------------- */

// function applyMovementLock(points) {
//   if (!points.length) return [];

//   // const RELOCK_CONFIG = {
//   //   relockSpeed: 3,     // km/h
//   //   relockSpread: 25,   // meters
//   //   relockWindow: 5     // last N points
//   // };
//   const RELOCK_CONFIG = {
//     relockSpeed: 2,
//     relockSpread: 18,
//     relockWindow: 5
//   };

//   let anchor = points[0];
//   let unlocked = false;

//   const out = [anchor];
//   let movingBuffer = [];

//   for (let i = 1; i < points.length; i++) {
//     const current = points[i];

//     /* =========================================
//        LOCKED MODE (Idle)
//     ========================================= */
//     if (!unlocked) {
//       const dist = getDistance(
//         anchor.lat,
//         anchor.lng,
//         current.lat,
//         current.lng
//       );
//       const speed = avgRecentSpeed(
//         [...out, current],
//         MOVEMENT_CONFIG.speedWindow
//       );

//       const shouldUnlock =
//         dist >= MOVEMENT_CONFIG.unlockDistance ||
//         speed >= MOVEMENT_CONFIG.unlockSpeed;

//       if (shouldUnlock) {
//         unlocked = true;
//         movingBuffer = [current];
//         out.push(current);
//       } else {
//         // DO NOTHING
//         // keep single anchor point only
//       }

//       continue;
//     }
//     /* =========================================
//        UNLOCKED MODE (Moving)
//     ========================================= */

//     out.push(current);
//     movingBuffer.push(current);

//     if (movingBuffer.length > RELOCK_CONFIG.relockWindow) {
//       movingBuffer.shift();
//     }

//     if (movingBuffer.length >= 3) {
//       const spread = getSpread(movingBuffer);
//       const speed = avgRecentSpeed(
//         movingBuffer,
//         movingBuffer.length
//       );

//       const shouldRelock =
//         spread <= RELOCK_CONFIG.relockSpread &&
//         speed <= RELOCK_CONFIG.relockSpeed;

//       if (shouldRelock) {
//         unlocked = false;
//         anchor = current;
//         movingBuffer = [];
//       }
//     }
//   }

//   return out;
// }

function applyMovementLockV3(points) {
  if (!points.length) return [];

  const cfg = {
    unlockDistance: 200,
    unlockSpeed: 4,

    relockSpeed: 2,
    relockSpread: 18,
    relockWindow: 5,

    jitterRadius: 12
  };

  let locked = true;
  let hasMoved = false;
  let anchor = points[0];
  let out = [anchor];
  let movingBuffer = [];

  for (let i = 1; i < points.length; i++) {
    const p = points[i];

    if (locked) {
      const dist = getDistance(
        anchor.lat, anchor.lng,
        p.lat, p.lng
      );

      const speed = avgRecentSpeed(
        [...out.slice(-3), p], 3
      );

      if (
        dist >= cfg.unlockDistance ||
        speed >= cfg.unlockSpeed
      ) {
        locked = false;
        movingBuffer = [p];
        out.push(p);
      }

      continue;
    }

    // moving mode
    out.push(p);
    movingBuffer.push(p);

    if (movingBuffer.length > cfg.relockWindow) {
      movingBuffer.shift();
    }

    const spread = getSpread(movingBuffer);
    const speed = avgRecentSpeed(
      movingBuffer,
      movingBuffer.length
    );

    if (
      spread <= cfg.relockSpread &&
      speed <= cfg.relockSpeed
    ) {
      locked = true;
      anchor = p;

      // compress destination cluster
      out[out.length - 1] = {
        ...p,
        lat: anchor.lat,
        lng: anchor.lng
      };

      movingBuffer = [];
    }
  }

  return out;
}


/* =========================================================
   ENTERPRISE GPS ENGINE
========================================================= */

function normalizePoints(pathLocations) {
  return [...pathLocations]
    .map(p => ({
      lat: Number(p.Latitude),
      lng: Number(p.Longitude),
      ts: getTs(p),
      captured_Date: p.captured_Date
    }))
    .filter(
      p =>
        !isNaN(p.lat) &&
        !isNaN(p.lng) &&
        p.lat !== 0 &&
        p.lng !== 0 &&
        p.ts
    )
    .sort((a, b) => a.ts - b.ts);
}

function removeDuplicates(points) {
  if (!points.length) return [];

  const out = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const prev = out[out.length - 1];

    const d = getDistance(
      prev.lat,
      prev.lng,
      points[i].lat,
      points[i].lng
    );

    if (d >= 2) out.push(points[i]);
  }

  return out;
}

function removeImpossibleJumps(points, maxKmh = 130) {
  if (!points.length) return [];

  const out = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const prev = out[out.length - 1];

    const meters = getDistance(
      prev.lat,
      prev.lng,
      points[i].lat,
      points[i].lng
    );

    const sec = (points[i].ts - prev.ts) / 1000;
    if (sec <= 0) continue;

    const kmh = (meters / sec) * 3.6;

    if (kmh <= maxKmh) out.push(points[i]);
  }

  return out;
}

function smoothPath(points, alpha = 0.28) {
  if (points.length < 2) return points;

  const out = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const prev = out[out.length - 1];

    out.push({
      ...points[i],
      lat: prev.lat + alpha * (points[i].lat - prev.lat),
      lng: prev.lng + alpha * (points[i].lng - prev.lng)
    });
  }

  return out;
}

function simplify(points, minMeters = 5) {
  if (points.length < 2) return points;

  const out = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const prev = out[out.length - 1];

    const d = getDistance(
      prev.lat,
      prev.lng,
      points[i].lat,
      points[i].lng
    );

    if (d >= minMeters) out.push(points[i]);
  }

  return out;
}

function getSpread(points) {
  if (points.length < 2) return 0;

  let max = 0;

  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const d = getDistance(
        points[i].lat,
        points[i].lng,
        points[j].lat,
        points[j].lng
      );

      if (d > max) max = d;
    }
  }

  return max;
}

function buildEnterpriseTrack(pathLocations) {
  let pts = pathLocations;

  if (DEBUG_TRACK.normalize) {
    pts = normalizePoints(pts);
  }

  if (DEBUG_TRACK.removeDuplicates) {
    pts = removeDuplicates(pts);
  }

  pts = applyMovementLockV3(pts);
  console.log("Points",
    pts.map(p => ({
      lat: p.lat,
      lng: p.lng
    }))
  );

  

  if (DEBUG_TRACK.removeJumps) {
    pts = removeImpossibleJumps(pts, 130);
  }

  if (DEBUG_TRACK.smooth) {
    pts = smoothPath(pts, 0.28);
  }

  if (DEBUG_TRACK.simplify) {
    pts = simplify(pts, 3);
  }

  const spread = getSpread(pts);

  const isIdle = DEBUG_TRACK.idleCheck
    ? spread < 12 && pts.length <= 2
    : false;

  return {
    rawPoints: pts,
    isIdle,
    spread,
    points: pts.length > 1
      ? pts.map(p => [p.lat, p.lng])
      : [],
    lastPoint: pts.length
      ? pts[pts.length - 1]
      : null
  };
}
/* =========================================================
   MAP HELPERS
========================================================= */

function FitBounds({ positions }) {
  const map = useMap();

  useEffect(() => {
    if (positions.length) {
      map.fitBounds(L.latLngBounds(positions), {
        padding: [40, 40]
      });
    }
  }, [positions, map]);

  return null;
}

function ZoomToPoint({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.flyTo(
        [position.lat, position.lng],
        16,
        { duration: 1.2 }
      );
    }
  }, [position, map]);

  return null;
}

function AnimatedPolyline({ positions }) {
  return (
    <>
      <Polyline
        positions={positions}
        pathOptions={{
          color: "#16a34a",
          weight: 10,
          opacity: 0.12
        }}
      />

      <Polyline
        positions={positions}
        pathOptions={{
          color: "#16a34a",
          weight: 3,
          opacity: 0.95
        }}
      />
    </>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function MapView({
  pathLocations = [],
  timeline = [],
  activeTab = "today",
  selectedCustomerPos = null,
  liveUser = null
}) {
  const mapRef = useRef();
  const [animatedPos, setAnimatedPos] = useState(null);

  const track = useMemo(
    () => buildEnterpriseTrack(pathLocations),
    [pathLocations]
  );

  const displayPath = track.points;

  useEffect(() => {
    // if (track.lastPoint) {
        if (liveUser?.lat && liveUser?.lng) {
      setAnimatedPos([
        Number(liveUser.lat),
        Number(liveUser.lng)
      ]);
    } else if (track.lastPoint) {
      setAnimatedPos([
        track.lastPoint.lat,
        track.lastPoint.lng
      ]);
    }
  }, [track, liveUser]);

  const validLocations = pathLocations.filter(p => {
    const lat = Number(p.Latitude);
    const lng = Number(p.Longitude);

    return (
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat !== 0 &&
      lng !== 0
    );
  });

  const startTime = formatTime(
    validLocations[0]?.captured_Date
  );

  const endTime = formatTime(
    validLocations[validLocations.length - 1]
      ?.captured_Date
  );

  const visitMarkers = timeline
    .filter(
      x =>
        x.type === "VISIT" &&
        Number(x.Latitude) &&
        Number(x.Longitude)
    )
    .map(x => ({
      lat: Number(x.Latitude),
      lng: Number(x.Longitude),
      name: x.CustomerName || "Visit",
      time: formatTime(x.captured_Date)
    }));

  const idleMarkers = timeline
    .filter(
      x =>
        x.type === "IDLE" &&
        Number(x.Latitude) &&
        Number(x.Longitude)
    )
    .map(x => ({
      lat: Number(x.Latitude),
      lng: Number(x.Longitude),
      time: x.captured_Date,
      duration: x.duration_seconds
    }));

  const center =
    displayPath[0] ||
    animatedPos ||
    [23.01, 72.34];

  return (
    <MapContainer
      center={center}
      zoom={13}
      zoomControl={false}
      whenCreated={m => (mapRef.current = m)}
      style={{ height: "500px" }}
    >
      <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />

      <ZoomControl position="bottomright" />

      {displayPath.length > 0 && (
        <FitBounds positions={displayPath} />
      )}

      {selectedCustomerPos && <ZoomToPoint position={selectedCustomerPos} />}
      {selectedCustomerPos && (
        <Marker position={[selectedCustomerPos.lat, selectedCustomerPos.lng]} icon={highlightIcon}>
          <Tooltip direction="top" offset={[0, -14]}>
            <div className="placeicon">🏢</div>
            <div className="map-tooltip">
              <strong>{selectedCustomerPos.name}</strong>
              {selectedCustomerPos.time && <span>{selectedCustomerPos.time}</span>}
            </div>
          </Tooltip>
        </Marker>
      )}

      {/* START */}
      {/* ── Start marker ──────────────────────────────────────── */}
      {displayPath[0] && (
        <Marker position={displayPath[0]} icon={startIconStatic}>
          <Tooltip direction="right" offset={[0, 0]}>
            <div className="placeicon">👨🏻</div>
            <div className="map-tooltip">
              <strong>Start</strong>
              {startTime && <span>{startTime}</span>}
            </div>
          </Tooltip>
        </Marker>
      )}


      {/* END */}
      {/* ── End marker — historical only ───────────────────────── */}
      {displayPath.length > 1 && activeTab !== "today" && (
        <Marker position={displayPath[displayPath.length - 1]} icon={endIconStatic}>
          <Tooltip direction="left" offset={[0, 0]}>
            <div className="placeicon">📌</div>
            <div className="map-tooltip">
              <strong>End</strong>
              {endTime && <span>{endTime}</span>}
            </div>
          </Tooltip>
        </Marker>
      )}


      {/* LIVE */}
      {animatedPos && activeTab === "today" && (
        <Marker position={animatedPos} icon={movingIcon}>
          <Tooltip direction="top" offset={[0, -10]}>
            <div className="placeicon">📡</div>
            <div className="map-tooltip">
              <strong>Live Tracking</strong>
            </div>
          </Tooltip>
        </Marker>
      )}

      {/* PATH */}
      {/* {displayPath.length > 1 && (
        <AnimatedPolyline
          positions={displayPath}
        />
      )} */}
      {DEBUG_TRACK.drawlines && displayPath.length > 1 && (
        <AnimatedPolyline positions={displayPath} />
      )}
      {DEBUG_TRACK.drawpoints &&
        track.rawPoints.map((p, i) => (
          <Marker
            key={i}
            position={[p.lat, p.lng]}
            icon={L.divIcon({
              html: `<div style="
          width:8px;
          height:8px;
          border-radius:50%;
          background:#16a34a;
          border:1px solid white;
        "></div>`,
              iconSize: [8, 8],
              iconAnchor: [4, 4],
              className: ""
            })}
          >
            <Tooltip direction="top">
              Point {i + 1} @ {p.captured_Date && formatTime(p.captured_Date)}
            </Tooltip>
          </Marker>
        ))}
      {/* VISITS */}
      {visitMarkers.map((v, i) => (
        <Marker key={`visit-${i}`} position={[v.lat, v.lng]} icon={makeVisitIcon()}>
          <Tooltip direction="top" offset={[0, -10]}>
            <div className="placeicon">👨🏻</div>
            <div className="map-tooltip">
              <strong>{v.name}</strong>
              <span>{v.time}</span>
            </div>
          </Tooltip>
        </Marker>
      ))}

      {/* IDLE */}
      {idleMarkers.map((idle, i) => (
        <Marker key={`idle-${i}`} position={[idle.lat, idle.lng]} icon={idleIcon}>          <Tooltip direction="top">
            <div className="placeicon">🕐</div>
            <div className="map-tooltip">
              <strong>Idle</strong>
              <span>
                {idle.time
                  ? new Date(idle.time)
                    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
                    .toLowerCase()
                  : ""}
                {idle.duration ? ` (${fmtIdleDuration(idle.duration)})` : ""}
              </span>
            </div>
          </Tooltip>
        </Marker>
      ))}

      <LocateButton lastPos={animatedPos} />
    </MapContainer>
  );
}